import { tourFunctions } from "../tour-functions";
const { getIndex } = tourFunctions.currentScene;
const getCurrentSceneIndex = getIndex;

// Preloader.js
export class Preloader {
  constructor(tour, directions = ["u", "d", "f", "b", "l", "r"]) {
    this.tour = tour;
    this.directions = directions;
    this.preloadPromises = [];
    this.scenes = tour.scenes?.filter((scene) => !scene.hidden);
    this.isStopped = false; // flag to indicate stop
  }

  stop() {
    const wasStopped = this.isStopped;
    this.preloadPromises = []; // Clear the promises array
    this.isStopped = true; // Set flag to true to stop preloading
    if (!wasStopped) {
      console.log("[Preloader] stop() — preload cancelled");
    }
  }

  preloadImage(url) {
    if (this.isStopped) return; // If stopped, return early
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = resolve;
      img.onerror = reject;
    });
  }

  async preloadImages(urls) {
    if (this.isStopped) return; // Stop if the process has been canceled
    this.preloadPromises = urls.map((url) => this.preloadImage(url));

    try {
      await Promise.all(this.preloadPromises);
    } catch (error) {
      console.error("Error preloading images", error);
    }
  }

  getSceneImageUrls(scene) {
    const urls = [];
    // Thumbnail
    urls.push(scene.thumbUrl);
    // Preview Image
    urls.push(scene.previewUrl);
    // Pano Images
    scene.images.forEach((image) => {
      this.directions.forEach((direction) => {
        const url = image.url.replace("%s", direction);
        urls.push(url);
      });
    });
    return urls;
  }

  async preloadScene(sceneIndex) {
    if (sceneIndex < 0 || sceneIndex >= this.scenes.length) {
      console.error("Invalid scene index");
      return;
    }

    const scene = this.scenes[sceneIndex];
    const urls = this.getSceneImageUrls(scene);
    await this.preloadImages(urls);
  }

  async preloadFirstScene() {
    await this.preloadScene(0);
  }

  /**
   * Preload the scene at the given index (e.g. start scene from URL hash/query).
   */
  async preloadStartScene(sceneIndex) {
    await this.preloadScene(sceneIndex);
  }

  async preloadNextScene() {
    const nextSceneIndex = (getCurrentSceneIndex() + 1) % this.scenes.length;
    await this.preloadScene(nextSceneIndex);
  }

  async preloadPreviousScene() {
    const prevSceneIndex =
      (getCurrentSceneIndex() - 1 + this.scenes.length) % this.scenes.length;
    await this.preloadScene(prevSceneIndex);
  }

  getHotspotImageUrls(hotspot) {
    const urls = [];
    // Popup Image
    if (hotspot.popupImage?.url) urls.push(hotspot.popupImage.url);
    // Gallery Images
    // consider gallery images for preloading
    // if (hotspot.galleryImages?.length > 0) {
    //   hotspot.galleryImages.forEach((image) => {
    //     urls.push(image.url);
    //   });
    // }
    return urls;
  }

  async preloadHotspot(hotspot) {
    const urls = this.getHotspotImageUrls(hotspot);
    await this.preloadImages(urls);
  }

  async preloadHotspots(hotspots) {
    const allUrls = hotspots.flatMap((hotspot) =>
      this.getHotspotImageUrls(hotspot)
    );
    await this.preloadImages(allUrls);
  }

  async preloadCurrentScene() {
    const scene = this.scenes[getCurrentSceneIndex()];
    await this.preloadHotspots(scene.hotspots);
  }

  async preloadThumbnails() {
    const urls = this.scenes.map((scene) => scene.thumbUrl);
    await this.preloadImages(urls);
  }

  /**
   * Preload the start scene (by index) and thumbnails.
   * @param {number} [startSceneIndex=0] - Index of the scene to preload (e.g. from URL startscene/hash). Default 0.
   */
  async initialPreload(startSceneIndex = 0) {
    const t0 = performance.now();
    const index = Math.max(
      0,
      Math.min(startSceneIndex, this.scenes.length - 1),
    );
    const scene = this.scenes[index];
    const startSceneUrlCount = scene ? this.getSceneImageUrls(scene).length : 0;
    const thumbnailUrlCount = this.scenes.length;
    console.log("[Preloader] initialPreload start", {
      startSceneIndex: index,
      visibleScenes: this.scenes.length,
      startSceneUrlCount,
      thumbnailUrlCount,
    });

    await this.preloadStartScene(index);
    if (this.isStopped) return;
    await this.preloadThumbnails();
    if (this.isStopped) return;

    console.log("[Preloader] initialPreload done", {
      elapsedMs: Math.round(performance.now() - t0),
      startSceneUrlCount,
      thumbnailUrlCount,
    });
  }

  /**
   * Preload full pano assets for every scene except the start index (already covered by initialPreload).
   * Runs sequentially to limit network contention. No-op if stopped.
   */
  async preloadRemainingScenes(startSceneIndex = 0) {
    if (!this.scenes?.length) return;

    const index = Math.max(
      0,
      Math.min(startSceneIndex, this.scenes.length - 1),
    );
    const t0 = performance.now();
    console.log("[Preloader] preloadRemainingScenes start", {
      skipIndex: index,
      sceneCount: this.scenes.length,
    });

    for (let i = 0; i < this.scenes.length; i++) {
      if (this.isStopped) {
        console.log("[Preloader] preloadRemainingScenes cancelled", {
          elapsedMs: Math.round(performance.now() - t0),
        });
        return;
      }
      if (i === index) continue;

      const sceneT0 = performance.now();
      await this.preloadScene(i);
      console.log("[Preloader] preloadRemainingScenes scene", {
        sceneIndex: i,
        elapsedMs: Math.round(performance.now() - sceneT0),
      });
    }

    console.log("[Preloader] preloadRemainingScenes done", {
      elapsedMs: Math.round(performance.now() - t0),
    });
  }
}
